import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import { TrendingUp, TrendingDown, CheckCircle, Warning } from '@mui/icons-material';
import { apiService } from '../../services/api';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface BurndownDataPoint {
  date: string;
  remaining: number;
  ideal: number;
  completed: number;
  added: number;
  removed: number;
  day: number;
}

interface SprintMetricsData {
  sprintId: string;
  sprintName: string;
  startDate: string;
  endDate: string;
  state: string;
  totalStoryPoints: number;
  completedStoryPoints: number;
  totalHours: number;
  completedHours: number;
  burndownData: BurndownDataPoint[];
  burnupData: any[];
  velocity: number;
  averageVelocity: number;
  isOnTrack: boolean;
  projectedCompletion: string | null;
  healthScore: number;
}

interface SprintBurndownChartProps {
  sprintId: string;
  height?: number;
  showControls?: boolean;
  onDataLoaded?: (data: SprintMetricsData) => void;
}

const SprintBurndownChart: React.FC<SprintBurndownChartProps> = ({
  sprintId,
  height = 400,
  showControls = true,
  onDataLoaded,
}) => {
  const [data, setData] = useState<SprintMetricsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metricType, setMetricType] = useState<'story_points' | 'hours'>('story_points');
  const [chartType, setChartType] = useState<'burndown' | 'burnup'>('burndown');

  const loadSprintMetrics = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.get<SprintMetricsData>(
        `/sprints/${sprintId}/metrics?metricType=${metricType}`
      );

      if (response.success && response.data) {
        setData(response.data);
        onDataLoaded?.(response.data);
      } else {
        setError('Failed to load sprint metrics');
      }
    } catch (err: any) {
      console.error('Error loading sprint metrics:', err);
      setError(err.response?.data?.message || 'Failed to load sprint metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sprintId) {
      loadSprintMetrics();
    }
  }, [sprintId, metricType]);

  const getChartData = () => {
    if (!data) return null;

    const chartData = chartType === 'burndown' ? data.burndownData : data.burnupData;
    const labels = chartData.map(point => {
      const date = new Date(point.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });

    if (chartType === 'burndown') {
      return {
        labels,
        datasets: [
          {
            label: 'Ideal Burndown',
            data: chartData.map(point => point.ideal),
            borderColor: 'rgb(99, 102, 241)',
            backgroundColor: 'rgba(99, 102, 241, 0.1)',
            borderDash: [5, 5],
            fill: false,
            tension: 0,
          },
          {
            label: 'Actual Remaining',
            data: chartData.map(point => point.remaining),
            borderColor: 'rgb(239, 68, 68)',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            fill: true,
            tension: 0.1,
          },
        ],
      };
    } else {
      return {
        labels,
        datasets: [
          {
            label: 'Scope',
            data: chartData.map(point => point.scope),
            borderColor: 'rgb(34, 197, 94)',
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            fill: false,
            tension: 0,
          },
          {
            label: 'Completed',
            data: chartData.map(point => point.completed),
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: true,
            tension: 0.1,
          },
          {
            label: 'Ideal Progress',
            data: chartData.map(point => point.ideal),
            borderColor: 'rgb(156, 163, 175)',
            backgroundColor: 'rgba(156, 163, 175, 0.1)',
            borderDash: [5, 5],
            fill: false,
            tension: 0,
          },
        ],
      };
    }
  };

  const getChartOptions = () => {
    const unit = metricType === 'story_points' ? 'Points' : 'Hours';
    
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top' as const,
        },
        title: {
          display: true,
          text: `${chartType === 'burndown' ? 'Burndown' : 'Burnup'} Chart - ${unit}`,
          font: {
            size: 16,
            weight: 'bold' as const,
          },
        },
        tooltip: {
          mode: 'index' as const,
          intersect: false,
          callbacks: {
            label: function(context: any) {
              return `${context.dataset.label}: ${context.parsed.y.toFixed(1)} ${unit}`;
            },
          },
        },
      },
      scales: {
        x: {
          display: true,
          title: {
            display: true,
            text: 'Sprint Days',
          },
        },
        y: {
          display: true,
          title: {
            display: true,
            text: unit,
          },
          beginAtZero: true,
        },
      },
      interaction: {
        mode: 'nearest' as const,
        axis: 'x' as const,
        intersect: false,
      },
    };
  };

  const getHealthStatus = () => {
    if (!data) return null;

    if (data.healthScore >= 80) {
      return { color: 'success', icon: <CheckCircle />, text: 'Excellent' };
    } else if (data.healthScore >= 60) {
      return { color: 'warning', icon: <Warning />, text: 'Good' };
    } else {
      return { color: 'error', icon: <TrendingDown />, text: 'Needs Attention' };
    }
  };

  const getVelocityTrend = () => {
    if (!data) return null;

    if (data.velocity > data.averageVelocity * 1.1) {
      return { color: 'success', icon: <TrendingUp />, text: 'Above Average' };
    } else if (data.velocity < data.averageVelocity * 0.9) {
      return { color: 'error', icon: <TrendingDown />, text: 'Below Average' };
    } else {
      return { color: 'info', icon: <TrendingUp />, text: 'On Track' };
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={height}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ height }}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ height }}>
        No sprint data available
      </Alert>
    );
  }

  const chartData = getChartData();
  const healthStatus = getHealthStatus();
  const velocityTrend = getVelocityTrend();

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        {showControls && (
          <Box mb={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Metric Type</InputLabel>
                  <Select
                    value={metricType}
                    label="Metric Type"
                    onChange={(e) => setMetricType(e.target.value as 'story_points' | 'hours')}
                  >
                    <MenuItem value="story_points">Story Points</MenuItem>
                    <MenuItem value="hours">Hours</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Chart Type</InputLabel>
                  <Select
                    value={chartType}
                    label="Chart Type"
                    onChange={(e) => setChartType(e.target.value as 'burndown' | 'burnup')}
                  >
                    <MenuItem value="burndown">Burndown</MenuItem>
                    <MenuItem value="burnup">Burnup</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Sprint Status Cards */}
        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" color="primary">
                {metricType === 'story_points' ? data.totalStoryPoints : data.totalHours}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total {metricType === 'story_points' ? 'Points' : 'Hours'}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" color="success.main">
                {metricType === 'story_points' ? data.completedStoryPoints : data.completedHours}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completed {metricType === 'story_points' ? 'Points' : 'Hours'}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" color="info.main">
                {data.velocity.toFixed(1)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Velocity/Day
              </Typography>
              {velocityTrend && (
                <Chip
                  size="small"
                  color={velocityTrend.color as any}
                  icon={velocityTrend.icon || undefined}
                  label={velocityTrend.text}
                  sx={{ mt: 1 }}
                />
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" color={data.isOnTrack ? 'success.main' : 'warning.main'}>
                {data.healthScore}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Health Score
              </Typography>
              {healthStatus && (
                <Chip
                  size="small"
                  color={healthStatus.color as any}
                  icon={healthStatus.icon || undefined}
                  label={healthStatus.text}
                  sx={{ mt: 1 }}
                />
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Chart */}
        <Box sx={{ height: height - 200, position: 'relative' }}>
          {chartData && (
            <Line data={chartData} options={getChartOptions()} />
          )}
        </Box>

        {/* Additional Info */}
        {data.projectedCompletion && (
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              Projected Completion: {new Date(data.projectedCompletion).toLocaleDateString()}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default SprintBurndownChart;
